import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { CustomerSite, FindDuplicateSitesRequest } from '../../models/customer-site';
import { NormalizedUsAddress } from '../../models/normalized-us-address';
import { CustomerService } from '../../services/customer.service';

export interface DuplicateCheckConfig {
    customerId: number;
    excludeSiteId?: number;
    chepGlid?: string;
    partySiteNumber?: string;
    address: NormalizedUsAddress;
}

export interface DuplicateCheckStatus {
    loading: boolean;
    sites: CustomerSite[];
}

@Component({
    selector: 'customer-site-duplicate-list',
    templateUrl: './customer-site-duplicate-list.component.html',
    styleUrls: ['./customer-site-duplicate-list.component.scss'],
    standalone: false
})
export class CustomerSiteDuplicateListComponent implements OnInit, OnChanges, OnDestroy {
    @Input() config: DuplicateCheckConfig | null = null;
    @Input() readonly = false;

    @Output() statusChange = new EventEmitter<DuplicateCheckStatus>();
    @Output() duplicateSelected = new EventEmitter<CustomerSite>();

    sites: CustomerSite[] = [];
    loading = false;
    selectedSite: CustomerSite | null = null;

    private destroyed = new Subject<void>();
    private checkRequest = new Subject<DuplicateCheckConfig>();

    constructor(private customerService: CustomerService) {}

    ngOnInit(): void {
        this.checkRequest.pipe(
            takeUntil(this.destroyed),
            tap(() => {
                this.loading = true;
                this.sites = [];
                this.selectedSite = null;
                this.statusChange.emit({ loading: true, sites: [] });
            }),
            switchMap(config => {
                const request: FindDuplicateSitesRequest = {
                    isUpdateOf: config.excludeSiteId,
                    chepGlobalLocationId: config.chepGlid,
                    partySiteNumber: config.partySiteNumber,
                    address1: config.address.address1,
                    city: config.address.city,
                    state: config.address.state,
                    postalCode: config.address.postalCode
                };

                return this.customerService.findDuplicateSites(config.customerId, request);
            })
        ).subscribe(results => {
            this.sites = results;
            this.loading = false;
            this.statusChange.emit({ loading: false, sites: results });
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['config']) {
            return;
        }

        if (this.config) {
            this.checkRequest.next(this.config);

            return;
        }

        this.sites = [];
        this.loading = false;
        this.selectedSite = null;
        this.statusChange.emit({ loading: false, sites: [] });
    }

    ngOnDestroy(): void {
        this.destroyed.next();
        this.destroyed.complete();
    }

    selectSite(site: CustomerSite): void {
        if (this.readonly) {
            return;
        }

        this.selectedSite = site;
        this.duplicateSelected.emit(site);
    }
}
